import DateFnsUtils from '@date-io/date-fns'
import { IconButton } from '@material-ui/core'
import CssBaseline from '@material-ui/core/CssBaseline'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { Close } from '@material-ui/icons'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import 'bootstrap/dist/css/bootstrap.min.css'
import { theme } from 'config'
import 'config/i18n'
import 'flag-icons/css/flag-icons.min.css'
import Cookie from 'js-cookie'
import { getCurrentVersion } from 'lib/api'
import { useWindowDimensions } from 'lib/hooks'
import meta from 'meta.json'
import { SnackbarProvider } from 'notistack'
import { createRef, useEffect, useState } from 'react'
import ReactPixel from 'react-facebook-pixel'
import TagManager from 'react-gtm-module'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import Context from 'utils/context'
import { CardtraderOauthConclude } from './domain/cardtrader/CardtraderOauth'
import { ShopifyOauthConclude, ShopifyOauthStart } from './domain/shopify/ShopifyOauth'
import {
  Account,
  Dashboard,
  FinishSignUp,
  ListingAndAppraisal,
  NotFound,
  SignIn,
  StockPricing,
} from './pages'
import { AdminPanel } from './pages/AdminPanel'
import { ApiDocs } from './pages/ApiDocs'
import { Backoffice } from './pages/Backoffice'
import { Debug } from './pages/Debug'
import { Ebay } from './pages/Ebay'
import { EndOnboarding } from './pages/EndOnboarding'
import { Exports } from './pages/Exports'
import { Inventory } from './pages/Inventory'
import { InventoryTransactions } from './pages/InventoryTransactions'
import { Jobs } from './pages/Jobs'
import { Kaboom } from './pages/Kaboom'
import { LoginRedirect } from './pages/LoginRedirect'
import { Onboarding } from './pages/Onboarding'
import { Orders } from './pages/Orders'
import { PricingSettings } from './pages/PricingSettings'
import { Relisting } from './pages/Relisting'
import { SignUp } from './pages/SignUp'
import { StaticPage } from './pages/StaticPage'
import { StaticPagePreview } from './pages/StaticPagePreview'
import { Status } from './pages/Status'
import { Taxation } from './pages/Taxation'
import { AppRerenderer } from './providers/AppRerender'
import { CardDataContextProvider } from './providers/CardDataProvider'
import { PriceGuideContextProvider } from './providers/PriceGuideProvider'
import { UserContextProvider } from './providers/UserProvider'

//@ts-ignore
global.appVersion = meta.version

const tagManagerArgs = {
  gtmId: 'GTM-NZKQF7X',
}

TagManager.initialize(tagManagerArgs)

export const verifyCorrectVersion = async (onIncorrectVersion: () => void): Promise<void> => {
  const response = await getCurrentVersion()
  //@ts-ignore
  if (response.data.version !== global.appVersion) {
    //@ts-ignore
    console.log('version mismatch', response.data.version, global.appVersion)
    if (caches) {
      caches.keys().then(function (names) {
        for (const name of names) caches.delete(name)
      })
    }
    onIncorrectVersion()
  } else {
    //@ts-ignore
    console.log('versions match', global.appVersion)
  }
}

function App(): JSX.Element {
  //const { username } = getUserFromCookie()

  const [isAuthenticated, setAuthenticated] = useState(!!Cookie.get('jwt'))
  const [shouldRedirectToNew, setShouldRedirectToNew] = useState(false)
  const { width } = useWindowDimensions()
  const isMobile = width < 1300
  const isPhone = width < 600

  useEffect(() => {
    //@ts-ignore
    verifyCorrectVersion(() => window.location.reload(true))
  }, [])

  const notistackRef = createRef()
  const onClickDismiss = (key: string | number) => () => {
    void (notistackRef.current as { closeSnackbar: (key: string | number) => void }).closeSnackbar(
      key
    )
  }

  return (
    <MuiThemeProvider theme={theme}>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <SnackbarProvider
          ref={notistackRef}
          action={(key) => (
            <IconButton
              data-testid="snackbar-close-btn"
              style={{ color: 'white' }}
              onClick={onClickDismiss(key)}
            >
              <Close />
            </IconButton>
          )}
          maxSnack={3}
        >
          <Context.Provider
            value={{
              ReactPixel,
              isAuthenticated,
              setAuthenticated,
              shouldRedirectToNew,
              setShouldRedirectToNew,
              isMobile,
              isPhone,
            }}
          >
            <BrowserRouter>
              <div className="app">
                <CssBaseline />
                <Switch>
                  <Route path="/api_docs/:version">
                    <ApiDocs />
                  </Route>
                  <Route path="/static_pages_preview/:id/:locale">
                    <StaticPagePreview />
                  </Route>
                  <Route path="/static_pages/:locale/:rest(.*)">
                    <StaticPage />
                  </Route>
                  <Route path="/signin">
                    <SignIn />
                  </Route>
                  <Route path="/signup">
                    <SignUp />
                  </Route>
                  <Route path="/login-redirect">
                    <LoginRedirect />
                  </Route>
                  <Route path="/b4ckoffic3/status">
                    <Status />
                  </Route>
                  <Route path="/shopify/oauth_start">
                    <ShopifyOauthStart />
                  </Route>
                  <Route path="/shopify/oauth_conclude">
                    <ShopifyOauthConclude />
                  </Route>
                  <Route path="/cardtrader/oauth_conclude">
                    <CardtraderOauthConclude />
                  </Route>
                  <UserContextProvider>
                    <Route path="/b4ckoffic3/controls">
                      <AdminPanel />
                    </Route>
                    <CardDataContextProvider>
                      <PriceGuideContextProvider>
                        <AppRerenderer>
                          <Route path="/onboarding">
                            <Onboarding />
                          </Route>
                          <Route path="/end-onboarding">
                            <EndOnboarding />
                          </Route>
                          {/*@ts-ignore*/}
                          <Route exact path="/">
                            <Dashboard />
                          </Route>
                          <Route path="/account">
                            <Account />
                          </Route>
                          <Route path="/finish-signup">
                            <FinishSignUp />
                          </Route>
                          <Route path="/listing-and-appraisal">
                            <ListingAndAppraisal />
                          </Route>
                          <Route path="/stock-pricing">
                            <StockPricing />
                          </Route>
                          <Route path="/d3bug">
                            <Debug />
                          </Route>
                          <Route path="/kaboom">
                            <Kaboom />
                          </Route>
                          <Route path="/pricing-settings">
                            <PricingSettings />
                          </Route>
                          <Route path="/orders">
                            <Orders />
                          </Route>
                          <Route path="/ebay">
                            <Ebay />
                          </Route>
                          <Route path="/inventory">
                            <Inventory />
                          </Route>
                          <Route path="/relisting">
                            <Relisting />
                          </Route>
                          <Route path="/taxation">
                            <Taxation />
                          </Route>
                          <Route path="/exports">
                            <Exports />
                          </Route>
                          <Route path="/inventoryTransactions">
                            <InventoryTransactions />
                          </Route>
                          <Route path="/jobs">
                            <Jobs />
                          </Route>
                          <Route path="/backoffice">
                            <Backoffice />
                          </Route>
                        </AppRerenderer>
                      </PriceGuideContextProvider>
                    </CardDataContextProvider>
                  </UserContextProvider>
                  <Route>
                    <NotFound />
                  </Route>
                </Switch>
              </div>
            </BrowserRouter>
          </Context.Provider>
        </SnackbarProvider>
      </MuiPickersUtilsProvider>
    </MuiThemeProvider>
  )
}

export default App
